import { Dispatch } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import { setupAuthInterceptor, setupRefreshTokenInterceptor } from "./api";
import {
  incrementTokenSetCount,
  setInterceptorIsReady,
} from "../store/apiConfigurationState";
import { hydrateFullPlanState } from "../store/realTimeUpdates/actions";
import { setCurrentUser } from "../store/currentUser";

type DecodedToken = {
  sub: string;
  preferred_username: string;
  name: string;
  email: string;
};

class AppInitializationService {
  private readonly dispatch: Dispatch<any>;

  constructor(dispatch: Dispatch<any>) {
    this.dispatch = dispatch;
  }

  /**
   * Resolves once the plan view state is hydrated. Hydration replaces that state with the backend's snapshot, so a
   * caller that starts a run must wait for it: otherwise a late snapshot from before the run wipes the run's state.
   */
  configureAppInitialization = (
    token: string,
    refreshToken: string,
  ): Promise<unknown> => {
    localStorage.setItem("access_token", token);
    localStorage.setItem("refresh_token", refreshToken);

    const decoded = jwtDecode<DecodedToken>(token);
    this.dispatch(
      setCurrentUser({
        sub: decoded.sub,
        preferredUsername: decoded.preferred_username,
        name: decoded.name ?? decoded.preferred_username,
        email: decoded.email,
      }),
    );
    const tokenSetIncrement = () => this.dispatch(incrementTokenSetCount());

    setupAuthInterceptor(token);
    setupRefreshTokenInterceptor(tokenSetIncrement);

    // With the thunk middleware, dispatching the thunk returns its promise
    const hydration = Promise.resolve(this.dispatch(hydrateFullPlanState()));
    this.dispatch(setInterceptorIsReady());
    return hydration;
  };
}

export default AppInitializationService;
